'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { MultiSelect, type LoadOptionsResponse } from '@shared/components/ui/MultiSelect';
import { ProductCombobox } from '@shared/components/ui/ProductCombobox/ProductCombobox';
import { usePermissions } from '@shared/hooks/usePermissions';
import { cn } from '@shared/utils/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStockMovementsFilters } from '../../hooks/useStockMovementsFilters';
import type { ReferenceIdColumn } from '../../utils/referenceIdFilters';

interface StockMovementsFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

type ReferenceFilterConfig = {
  column: ReferenceIdColumn;
  labelKey: string;
  loadOptions: (params: { search: string; page: number; limit: number }) => Promise<LoadOptionsResponse>;
  searchPlaceholderKey: string;
  dataTestId: string;
};

export const StockMovementsFiltersPanel = ({
  initialConditions = [],
  onChange,
  className,
}: StockMovementsFiltersPanelProps) => {
  const { t } = useTranslation('stockmovementsModule');
  const { getTeamType } = usePermissions();
  const isTenant3pl = getTeamType() === 'tenant_3pl';

  const {
    filters,
    handleProductChange,
    handleTeamChange,
    handleQtyChange,
    handleEventChange,
    handleDescriptionChange,
    handleUserChange,
    handleDateChange,
    handleReferenceFilterChange,
    clearAllFilters,
    loadBatchesForMultiSelect,
    loadOrdersForMultiSelect,
    loadPreparationsForMultiSelect,
    loadReturnedReceptionsForMultiSelect,
    loadEntriesForMultiSelect,
    loadExtraWorkOrdersForMultiSelect,
    loadLocationOrdersForMultiSelect,
    loadTeamsForMultiSelect,
    users,
    searchProducts,
    isLoadingUsers,
  } = useStockMovementsFilters({ initialConditions, onChange });

  const eventTypeOptions = useMemo(
    () => [
      { value: '2', label: t('filters.options.2') },
      { value: '8', label: t('filters.options.8') },
      { value: '10', label: t('filters.options.10') },
      { value: '12', label: t('filters.options.12') },
      { value: '13', label: t('filters.options.13') },
    ],
    [t],
  );

  const allReferenceFilters: ReferenceFilterConfig[] = useMemo(
    () => [
      {
        column: 'order_id',
        labelKey: 'filters.order_id',
        loadOptions: loadOrdersForMultiSelect,
        searchPlaceholderKey: 'filters.order_search_placeholder',
        dataTestId: 'filter-order-id',
      },
      {
        column: 'stock_location_order_id',
        labelKey: 'filters.stock_location_order_id',
        loadOptions: loadPreparationsForMultiSelect,
        searchPlaceholderKey: 'filters.preparation_search_placeholder',
        dataTestId: 'filter-stock-location-order-id',
      },
      {
        column: 'returned_reception_id',
        labelKey: 'filters.returned_reception_id',
        loadOptions: loadReturnedReceptionsForMultiSelect,
        searchPlaceholderKey: 'filters.returned_reception_search_placeholder',
        dataTestId: 'filter-returned-reception-id',
      },
      {
        column: 'entry_id',
        labelKey: 'filters.entry_id',
        loadOptions: loadEntriesForMultiSelect,
        searchPlaceholderKey: 'filters.entry_search_placeholder',
        dataTestId: 'filter-entry-id',
      },
      {
        column: 'extra_work_order_id',
        labelKey: 'filters.extra_work_order_id',
        loadOptions: loadExtraWorkOrdersForMultiSelect,
        searchPlaceholderKey: 'filters.extra_work_order_search_placeholder',
        dataTestId: 'filter-extra-work-order-id',
      },
      {
        column: 'location_order_id',
        labelKey: 'filters.location_order_id',
        loadOptions: loadLocationOrdersForMultiSelect,
        searchPlaceholderKey: 'filters.location_order_search_placeholder',
        dataTestId: 'filter-location-order-id',
      },
      {
        column: 'batch_id',
        labelKey: 'table.batch',
        loadOptions: loadBatchesForMultiSelect,
        searchPlaceholderKey: 'filters.batch_search_placeholder',
        dataTestId: 'filter-batch-id',
      },
    ],
    [
      loadOrdersForMultiSelect,
      loadPreparationsForMultiSelect,
      loadReturnedReceptionsForMultiSelect,
      loadEntriesForMultiSelect,
      loadExtraWorkOrdersForMultiSelect,
      loadLocationOrdersForMultiSelect,
      loadBatchesForMultiSelect,
    ],
  );

  const referenceFilters = useMemo(
    () =>
      isTenant3pl
        ? allReferenceFilters
        : allReferenceFilters.filter((f) => f.column === 'order_id' || f.column === 'batch_id'),
    [allReferenceFilters, isTenant3pl],
  );

  const [localDescription, setLocalDescription] = useState(filters.description);
  const [localQty, setLocalQty] = useState(filters.qty);

  useEffect(() => {
    setLocalDescription(filters.description);
    setLocalQty(filters.qty);
  }, [filters.description, filters.qty]);

  const applyDescriptionFilter = () => {
    if (localDescription !== filters.description) {
      handleDescriptionChange(localDescription);
    }
  };

  const applyQtyFilter = (type: 'min' | 'max', value: string) => {
    if (localQty[type] !== filters.qty[type]) {
      handleQtyChange(type, value);
    }
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.product_id.length > 0) count++;
    if (isTenant3pl && filters.team_id.length > 0) count++;
    if (filters.qty.min || filters.qty.max) count++;
    if (filters.event_id.length > 0) count++;
    if (filters.description.trim()) count++;
    if (isTenant3pl && filters.user_id.length > 0) count++;
    if (filters.created_at?.from && filters.created_at?.to) count++;
    referenceFilters.forEach(({ column }) => {
      if (filters[column].length > 0) count++;
    });
    return count;
  }, [filters, referenceFilters, isTenant3pl]);

  const notFound = t('filters.notFound', 'Sin resultados');

  return (
    <div className={cn('flex flex-col', className)}>
      <FilterSection title={t('filters.basic_info', 'Información básica')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('table.product')}>
            <ProductCombobox
              value={filters.product_id[0] || ''}
              setValue={(val) => handleProductChange(val ? [val] : [])}
              searchFn={searchProducts}
              placeholder={t('filters.product_placeholder', 'Buscar producto...')}
              emptyMessage={notFound}
              dataTestId="filter-product"
            />
          </FilterField>

          {isTenant3pl ? (
            <FilterField label={t('table.client')}>
              <MultiSelect
                loadOptions={loadTeamsForMultiSelect}
                value={filters.team_id}
                onValueChange={handleTeamChange}
                placeholder={t('filters.select_client', 'Seleccionar cliente')}
                emptyText={notFound}
                noResultsText={notFound}
                searchPlaceholder={t('filters.client_search_placeholder', 'Buscar cliente...')}
                dataTestId="filter-team"
                triggerClassName="h-[38px]"
                pageSize={20}
              />
            </FilterField>
          ) : null}

          <FilterField label={t('table.type')}>
            <MultiSelect
              options={eventTypeOptions}
              value={filters.event_id}
              onValueChange={handleEventChange}
              placeholder={t('filters.select_type', 'Seleccionar tipo')}
              emptyText={notFound}
              dataTestId="filter-event"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          {isTenant3pl ? (
            <FilterField label={t('table.user')}>
              <MultiSelect
                options={users}
                value={filters.user_id}
                onValueChange={handleUserChange}
                placeholder={isLoadingUsers ? t('common.loading') : t('filters.user_placeholder', 'Buscar usuario...')}
                emptyText={notFound}
                disabled={isLoadingUsers}
                dataTestId="filter-user"
                triggerClassName="h-[38px]"
              />
            </FilterField>
          ) : null}
        </div>
      </FilterSection>

      {referenceFilters.length > 0 ? (
        <FilterSection title={t('filters.references', 'Referencias')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {referenceFilters.map(({ column, labelKey, loadOptions, searchPlaceholderKey, dataTestId }) => (
              <FilterField key={column} label={t(labelKey)}>
                <MultiSelect
                  loadOptions={loadOptions}
                  value={filters[column]}
                  onValueChange={(values) => handleReferenceFilterChange(column, values)}
                  placeholder={t('filters.select_reference', 'Seleccionar...')}
                  emptyText={notFound}
                  noResultsText={notFound}
                  searchPlaceholder={t(searchPlaceholderKey)}
                  dataTestId={dataTestId}
                  triggerClassName="h-[38px]"
                  pageSize={20}
                />
              </FilterField>
            ))}
          </div>
        </FilterSection>
      ) : null}

      <FilterSection title={t('filters.details', 'Detalles')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('table.amount')}>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={localQty.min}
                onChange={(e) => setLocalQty((prev) => ({ ...prev, min: e.target.value }))}
                onBlur={() => applyQtyFilter('min', localQty.min)}
                onKeyDown={(e) => e.key === 'Enter' && applyQtyFilter('min', localQty.min)}
                placeholder="Min"
                className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
              <span className="text-muted-foreground">-</span>
              <input
                type="number"
                value={localQty.max}
                onChange={(e) => setLocalQty((prev) => ({ ...prev, max: e.target.value }))}
                onBlur={() => applyQtyFilter('max', localQty.max)}
                onKeyDown={(e) => e.key === 'Enter' && applyQtyFilter('max', localQty.max)}
                placeholder="Max"
                className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </div>
          </FilterField>

          <FilterField label={t('table.date')}>
            <DateRangePicker
              initialDateFrom={filters.created_at?.from}
              initialDateTo={filters.created_at?.to}
              onUpdate={({ range }) => handleDateChange(range)}
              placeholder={t('filters.date_placeholder')}
              dataTestId="filter-date"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>

          <div className="col-span-1 sm:col-span-2">
            <FilterField label={t('table.description')}>
              <input
                type="text"
                value={localDescription}
                onChange={(e) => setLocalDescription(e.target.value)}
                onBlur={applyDescriptionFilter}
                onKeyDown={(e) => e.key === 'Enter' && applyDescriptionFilter()}
                placeholder={t('filters.description_placeholder')}
                className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                data-testid="filter-description"
              />
            </FilterField>
          </div>
        </div>
      </FilterSection>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-5 mt-5 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
          >
            Limpiar {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro' : 'filtros'}
          </Button>
        </div>
      )}
    </div>
  );
};

const FilterSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="space-y-3 pt-5 mt-5 border-t border-card-border first:mt-0 first:pt-0 first:border-t-0">
    <h4 className="text-xs font-semibold text-complementary-text uppercase tracking-wide">{title}</h4>
    {children}
  </section>
);

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
